import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCategoryOptionsFromCatalog,
  getSubcategoryOptionsFromCatalog,
} from "../utils/catalogListFilterOptions";

/**
 * Keeps the master category tree stable while the user toggles filters.
 * The full category list is snapshotted on `searchKey` change; subcategory options
 * narrow to selected categories (or show all when none are selected).
 */
export function useStableCatalogFilterOptions(catalog, searchKey, selectedCategories = null) {
  const prevSearchKeyRef = useRef(searchKey);
  const staleCatalogRef = useRef(null);
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  const selectedSet = useMemo(() => {
    if (selectedCategories instanceof Set) return selectedCategories;
    if (Array.isArray(selectedCategories)) {
      return new Set(selectedCategories.map((x) => String(x || "").trim()).filter(Boolean));
    }
    return new Set();
  }, [selectedCategories]);

  const [snapshotCatalog, setSnapshotCatalog] = useState(() => catalog);

  const liveCategoryOptions = useMemo(
    () => getCategoryOptionsFromCatalog(catalog),
    [catalog]
  );
  const liveSubcategoryOptions = useMemo(
    () => getSubcategoryOptionsFromCatalog(catalog, [], selectedSet),
    [catalog, selectedSet]
  );

  useEffect(() => {
    if (prevSearchKeyRef.current === searchKey) return;
    staleCatalogRef.current = catalogRef.current;
    prevSearchKeyRef.current = searchKey;
    setSnapshotCatalog(null);
  }, [searchKey]);

  useEffect(() => {
    if (staleCatalogRef.current != null && catalog === staleCatalogRef.current) {
      return;
    }
    if (!liveCategoryOptions.length && !liveSubcategoryOptions.length) {
      return;
    }
    setSnapshotCatalog(catalog);
  }, [catalog, liveCategoryOptions.length, liveSubcategoryOptions.length]);

  return useMemo(() => {
    const activeCatalog = snapshotCatalog || catalog;
    const categoryOptions = getCategoryOptionsFromCatalog(activeCatalog);
    const subcategoryOptions = getSubcategoryOptionsFromCatalog(
      activeCatalog,
      [],
      selectedSet
    );

    if (categoryOptions.length > 0 || subcategoryOptions.length > 0) {
      return { categoryOptions, subcategoryOptions };
    }

    return {
      categoryOptions: liveCategoryOptions,
      subcategoryOptions: liveSubcategoryOptions,
    };
  }, [
    snapshotCatalog,
    catalog,
    selectedSet,
    liveCategoryOptions,
    liveSubcategoryOptions,
  ]);
}
